import { useState, useRef, useMemo, useEffect } from "react";
import {
    validateNumberUsage,
    evaluateExpression,
    checkResult,
} from "../gameLogic";
import {
    fetchAllNumbers,
    saveCompletionStatus,
    getCompletionStatus,
    getTodayDate,
    saveSuccessfulExpression,
    getSuccessfulExpression,
} from "../services/api";
import {
    updateStreak,
    checkAndResetStreak,
    getStreakData,
} from "../services/streakService";

export function useGameLogic() {
    const [currentDate] = useState<string>(getTodayDate());
    const [numberCount, setNumberCount] = useState<4 | 5 | 6>(4);

    // Initialize expression from localStorage if available
    const [expression, setExpression] = useState<string>(() => {
        return getSuccessfulExpression(currentDate, 4) || "";
    });

    const [liveResult, setLiveResult] = useState<{
        value: string;
        valid: boolean;
        isEmpty: boolean;
    }>({ value: "", valid: false, isEmpty: true });
    const [showHelp, setShowHelp] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const celebrationTimerRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const [highlightedNumbers, setHighlightedNumbers] = useState<Set<number>>(
        new Set()
    );
    const [showSuccessResult, setShowSuccessResult] = useState<boolean>(false);
    const [completionStatus, setCompletionStatus] = useState<{
        [key: number]: boolean;
    }>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showStreakDialog, setShowStreakDialog] = useState<boolean>(false);
    const [currentStreak, setCurrentStreak] = useState<number>(0);

    // Store all numbers fetched once
    const [allNumbers, setAllNumbers] = useState<{
        4: number[];
        5: number[];
        6: number[];
    }>({ 4: [], 5: [], 6: [] });

    // Get current numbers based on selected count
    const numbers = useMemo(() => {
        return allNumbers[numberCount];
    }, [allNumbers, numberCount]);

    const availableNumbers = useMemo(() => {
        return new Set(numbers);
    }, [numbers]);

    // Load all numbers once when component mounts
    useEffect(() => {
        loadAllNumbers();
        // Check and reset streak if needed on mount
        checkAndResetStreak();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load completion status when date changes
    useEffect(() => {
        const status = getCompletionStatus(currentDate);
        setCompletionStatus(status);
        if (status[4] && status[5] && status[6]) {
            // All completed - load and show streak
            const streakData = getStreakData();
            setCurrentStreak(streakData.currentStreak);
            setShowStreakDialog(true);
        }
    }, [currentDate]);

    // Restore expression and success state when numbers are loaded or tab changes
    useEffect(() => {
        if (numbers.length === 0) return; // Wait for numbers to load

        const savedExpression = getSuccessfulExpression(
            currentDate,
            numberCount
        );
        const isCompleted = completionStatus[numberCount] === true;

        if (savedExpression && isCompleted) {
            // Restore the successful expression
            setExpression(savedExpression);

            // Re-evaluate to show success state
            const evaluation = evaluateExpression(savedExpression);
            if (!evaluation.error && evaluation.result !== undefined) {
                const resultValue = evaluation.result;
                const validation = validateNumberUsage(
                    savedExpression,
                    numbers
                );
                const isCorrect = validation.valid && checkResult(resultValue);

                if (isCorrect) {
                    setLiveResult({
                        value: `= ${resultValue
                            .toFixed(2)
                            .replace(/\.?0+$/, "")}`,
                        valid: true,
                        isEmpty: false,
                    });

                    // Show all numbers as highlighted
                    const allHighlighted = new Set<number>();
                    numbers.forEach((_, index) => allHighlighted.add(index));
                    setHighlightedNumbers(allHighlighted);
                    setShowSuccessResult(true);
                }
            }
        }
    }, [numbers, numberCount, currentDate, completionStatus]);

    // Check if already completed
    const isCompleted = useMemo(() => {
        return completionStatus[numberCount] === true;
    }, [completionStatus, numberCount]);

    const loadAllNumbers = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch once - all numbers come back in single request
            const data = await fetchAllNumbers(currentDate);

            setAllNumbers({
                4: data[4],
                5: data[5],
                6: data[6],
            });
        } catch (err) {
            setError("Failed to load numbers. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Play a ding sound at the specified frequency
    const playDing = (frequency: number) => {
        // Reuse or create AudioContext
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
        }

        const audioContext = audioContextRef.current;
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = "sine";

        // Quick attack and decay for "ding" effect
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(
            0.5,
            audioContext.currentTime + 0.01
        );
        gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.3
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
    };

    const updateExpression = (newExpression: string) => {
        setExpression(newExpression);

        if (!newExpression.trim()) {
            setLiveResult({ value: "", valid: false, isEmpty: true });
            return;
        }

        const trimmedExpr = newExpression.trim();
        const endsWithOperator = /[+\-*/^(]$/.test(trimmedExpr);

        let exprToEvaluate = trimmedExpr;
        if (endsWithOperator) {
            exprToEvaluate = trimmedExpr.replace(/[+\-*/^(]+$/, "");
        }

        if (exprToEvaluate) {
            const evaluation = evaluateExpression(exprToEvaluate);
            if (!evaluation.error && evaluation.result !== undefined) {
                const resultValue = evaluation.result;
                const validation = validateNumberUsage(trimmedExpr, numbers);
                const isCorrect = validation.valid && checkResult(resultValue);

                setLiveResult({
                    value: `= ${resultValue.toFixed(2).replace(/\.?0+$/, "")}`,
                    valid: isCorrect,
                    isEmpty: false,
                });

                if (isCorrect) {
                    // Animate numbers turning green one by one
                    const highlighted = new Set<number>();
                    // Base frequency and increment for rising pitch
                    const baseFreq = 523; // C5
                    const freqIncrement = 131; // Increment for each step

                    numbers.forEach((_, index) => {
                        setTimeout(() => {
                            highlighted.add(index);
                            setHighlightedNumbers(new Set(highlighted));

                            // Play ding for this number with increasing pitch
                            const frequency = baseFreq + index * freqIncrement;
                            playDing(frequency);
                        }, index * 300);
                    });

                    // Show success result after all numbers are highlighted
                    setTimeout(() => {
                        setShowSuccessResult(true);

                        // Play final ding one octave higher than the last number
                        const lastNumberFreq =
                            baseFreq + (numbers.length - 1) * freqIncrement;
                        playDing(lastNumberFreq * 2);

                        // Mark as completed and save expression
                        saveCompletionStatus(currentDate, numberCount);
                        saveSuccessfulExpression(
                            currentDate,
                            numberCount,
                            trimmedExpr
                        );
                        const updatedStatus = getCompletionStatus(currentDate);
                        setCompletionStatus(updatedStatus);

                        // Check if all three difficulties are now completed
                        if (
                            updatedStatus[4] &&
                            updatedStatus[5] &&
                            updatedStatus[6]
                        ) {
                            // Update streak and show dialog
                            const streakResult = updateStreak(currentDate);
                            if (streakResult.isNewStreak) {
                                setCurrentStreak(streakResult.newStreak);
                                // Show streak dialog after a short delay
                                setTimeout(() => {
                                    setShowStreakDialog(true);
                                }, 500);
                            }
                        } else {
                            // Find next incomplete difficulty in order: 4 -> 5 -> 6
                            const nextIncomplete = [4, 5, 6].find(
                                (count) => !updatedStatus[count]
                            ) as 4 | 5 | 6 | undefined;

                            if (nextIncomplete) {
                                // Switch to next incomplete tab after a short delay
                                setTimeout(() => {
                                    handleNumberCountChange(nextIncomplete);
                                }, 1000);
                            }
                        }
                    }, numbers.length * 300 + 500);
                }
                return;
            }
        }
    };

    const handleExpressionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateExpression(e.target.value);
    };

    const handleKeyPress = (key: string) => {
        if (key === "⌫") {
            if (expression.length > 0) {
                updateExpression(expression.slice(0, -1));
            }
        } else if (key === "C") {
            updateExpression("");
        } else {
            updateExpression(expression + key);
        }
    };

    const handleNumberCountChange = (count: 4 | 5 | 6) => {
        if (celebrationTimerRef.current) {
            clearTimeout(celebrationTimerRef.current);
            celebrationTimerRef.current = null;
        }
        setNumberCount(count);

        // Check if this difficulty has a saved successful expression
        const savedExpression = getSuccessfulExpression(currentDate, count);
        const isCompleted = completionStatus[count] === true;

        if (savedExpression && isCompleted) {
            // Don't reset - will be restored by useEffect
            return;
        }

        // Reset expression when switching tabs if not completed
        setExpression("");
        setLiveResult({ value: "", valid: false, isEmpty: true });
        setHighlightedNumbers(new Set());
        setShowSuccessResult(false);
    };

    return {
        numbers,
        expression,
        numberCount,
        liveResult,
        showHelp,
        setShowHelp,
        showStreakDialog,
        setShowStreakDialog,
        currentStreak,
        inputRef,
        availableNumbers,
        handleExpressionChange,
        handleKeyPress,
        handleNumberCountChange,
        celebrationTimerRef,
        highlightedNumbers,
        showSuccessResult,
        loading,
        error,
        completionStatus,
        currentDate,
        isCompleted,
    };
}
